import { InputAdornment, TextField, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ReactNode } from "react";
import { Control, Controller, RegisterOptions, useFormState } from "react-hook-form";

type RulesType = Omit<
  RegisterOptions<any>,
  "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
>;

export const ControlledTextField = ({
  control,
  field,
  fieldLabel,
  helperText,
  rules,
  disabled,
  tooltip,
}: {
  field: string;
  control: Control<any>;
  fieldLabel?: string;
  helperText?: ReactNode;
  rules?: RulesType;
  disabled?: boolean;
  tooltip?: ReactNode;
}) => {
  const label =
    fieldLabel ??
    field.replace(/_/g, " ").replace(/\w\S*/g, w => w.replace(/^\w/, c => c.toUpperCase()));
  const inputLabelProps = { shrink: true };
  const { dirtyFields } = useFormState({ control });
  return (
    <Controller
      control={control}
      rules={rules}
      render={({ field: { onChange, onBlur, value }, fieldState: { error, invalid } }) => {
        const dirty = !!dirtyFields[field];
        return (
          <TextField
            label={label}
            size="small"
            onBlur={onBlur}
            onChange={onChange}
            value={value ?? ""}
            InputLabelProps={inputLabelProps}
            multiline
            fullWidth
            color={dirty ? "secondary" : "primary"}
            focused={dirty}
            helperText={error ? error.message : helperText}
            error={invalid}
            disabled={disabled}
            InputProps={
              tooltip
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title={tooltip}
                          arrow
                          slotProps={{
                            tooltip: {
                              sx: { bgcolor: "rgba(0, 0, 0, 0.92)", maxWidth: 360, p: 1.5 },
                            },
                            arrow: { sx: { color: "rgba(0, 0, 0, 0.92)" } },
                          }}
                        >
                          <InfoOutlinedIcon
                            fontSize="small"
                            color="action"
                            sx={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
          />
        );
      }}
      name={field}
    />
  );
};
